import { createContext, FCWithChildren, useCallback, useRef } from "react";
import { RealtimePayload, RealtimeUpdateFn, UseRealtimeSubscription } from "./use-realtime-subscription";
import { useSupabase } from "#next/hooks/use-supbase";
import { useIsUserLoggedIn } from "#next/hooks/use-is-user-logged-in";
import { TableName } from "#/types/supabase";
import { RealtimeChannel } from "@supabase/supabase-js";

export const realtimeEnabledTables = [
  "users",
  "commands",
  "user_subscriptions",
  "api_tokens"
] as const;

export type RealtimeEnabledTable = typeof realtimeEnabledTables[number] extends TableName ? typeof realtimeEnabledTables[number] : never;

export type OperationType = "Update" | "Delete" | "Insert";

type HandlerType = "onDeleteHandlers" | "onUpdateHandlers" | "onInsertHandlers";

type HandlerTypeToOperationType<K extends HandlerType> =
  K extends "onDeleteHandlers" ? "Delete"
    : K extends "onUpdateHandlers" ? "Update"
      : K extends "onInsertHandlers" ? "Insert"
        : never;

type HandlerWithId<
  T extends TableName,
  K extends OperationType
> = {
  id: string,
  handler: RealtimeUpdateFn<T, K>
};

type SubscribedHandlers = {
  [K in TableName]?: {
    channel: RealtimeChannel,
    onUpdateHandlers: HandlerWithId<K, "Update">[],
    onDeleteHandlers: HandlerWithId<K, "Delete">[],
    onInsertHandlers: HandlerWithId<K, "Insert">[]
  }
};

export interface RealtimeContext {
  subscribeToChannel<T extends RealtimeEnabledTable>(id: string, args: UseRealtimeSubscription<T>): void,
  unsubscribeFromChannel<T extends RealtimeEnabledTable>(id: string, args: Pick<UseRealtimeSubscription<T>, "table">): void
}

export const RealtimeContext = createContext<RealtimeContext>({
  subscribeToChannel() {
    throw new Error("subscribeToChannel not implemented");
  },
  unsubscribeFromChannel() {
    throw new Error("unsubscribeFromChannel not implemented");
  }
});

export const RealtimeProvider: FCWithChildren = ({ children }) => {
  const supabase = useSupabase();
  const { isLoggedIn } = useIsUserLoggedIn();
  const subscribedHandlers = useRef<SubscribedHandlers>({});
  const callHandlersWithPayload = useCallback(async <
    T extends RealtimeEnabledTable,
    O extends HandlerType
  >(
    payload: RealtimePayload<T, HandlerTypeToOperationType<O>>,
    tableName: T,
    operation: O
  ) => {
    const handler = subscribedHandlers.current[tableName] as SubscribedHandlers[T];
    if (!handler) {
      return;
    }
    const handlerPromises: (Promise<void> | void)[] = [];
    for (const fn of handler[operation]) {
      // TODO: figure out how to strongly type this at some point in the (probably distance) future.
      handlerPromises.push(fn.handler(payload as any));
    }
    await Promise.all(handlerPromises);
  }, [ ]);

  const subscribeToChannel = useCallback(<T extends RealtimeEnabledTable>(id: string, {
    table,
    onUpdate,
    onDelete,
    onInsert,
    logRealtime,
    condition
  }: UseRealtimeSubscription<T>) => {
    // Skip if user is not logged in or condition is explicitly false
    if (isLoggedIn !== true || condition === false) {
      return;
    }

    if (!subscribedHandlers.current[table]) {
      const channel = supabase.realtime.channel(table);
      subscribedHandlers.current[table] = {
        channel: channel,
        onDeleteHandlers: [],
        onUpdateHandlers: [],
        onInsertHandlers: []
      };
      channel.on("postgres_changes", { event: "UPDATE", schema: "public", table: table }, (payload: any) => callHandlersWithPayload(payload as any, table, "onUpdateHandlers"));
      // TODO: figure out how to strongly type the payload being passed into callHandlersWithPayload at some point in the (probably distance) future.
      channel.on("postgres_changes", { event: "DELETE", schema: "public", table: table }, (payload: any) => callHandlersWithPayload(payload as any, table, "onDeleteHandlers"));
      channel.on("postgres_changes", { event: "INSERT", schema: "public", table: table }, (payload: any) => callHandlersWithPayload(payload as any, table, "onInsertHandlers"));
      channel.subscribe(async (status: any) => {
        if (!logRealtime) {
          return;
        } else if (status === "CLOSED") {
          console.error(`Realtime subscriptions are not working for subscription to table ${table}\n(...Are you running this on localhost? If so, and realtime matters, please use an ngrok tunnel)`);
        } else if (status === "SUBSCRIBED") {
          console.log(`Subscribed to ${table} changes`);
        }
      });
    }

    const handler = subscribedHandlers.current[table] as SubscribedHandlers[T];
    if (!handler) {
      throw new Error("this should never happen");
    }

    if (onDelete) {
      handler.onDeleteHandlers.push({
        id,
        handler: onDelete
      });
    }
    if (onUpdate) {
      handler.onUpdateHandlers.push({
        id,
        handler: onUpdate
      });
    }
    if (onInsert) {
      handler.onInsertHandlers.push({
        id,
        handler: onInsert
      });
    }
  }, [ callHandlersWithPayload, isLoggedIn, supabase ]);

  const unsubscribeFromChannel = useCallback(<T extends RealtimeEnabledTable>(id: string, {
    table
  }: Pick<UseRealtimeSubscription<T>, "table">) => {
    if (isLoggedIn !== true) {
      return;
    }

    // Only attempt to access handlers if they exist
    const handler = subscribedHandlers.current[table] as SubscribedHandlers[T];
    if (!handler) {
      return; // Silently return instead of throwing an error
    }

    handler.onDeleteHandlers = handler.onDeleteHandlers.filter(x => x.id !== id);
    handler.onUpdateHandlers = handler.onUpdateHandlers.filter(x => x.id !== id);
    handler.onInsertHandlers = handler.onInsertHandlers.filter(x => x.id !== id);
  }, [ isLoggedIn ]);

  return (
    <RealtimeContext.Provider
      value={{
        subscribeToChannel,
        unsubscribeFromChannel
      }}>
      {children}
    </RealtimeContext.Provider>
  );
};
